import React, { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';

const CustomDialog = ({ open, onClose, children }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

const UserNotifications = ({ userId }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isBlinking, setIsBlinking] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [socket, setSocket] = useState(null);
  // Pre-load notification sound
  const notificationSound = new Audio('/notification.mp3');
  notificationSound.preload = 'auto';

  // Define status mapping
  const getStatusText = (status) => {
    const statusMap = {
      0: 'Pending',
      1: 'Approved',
      2: 'Accepted',
      3: 'Rejected',
      4: 'In Progress',
      5: 'Completed',
      7: 'Declined'
    };
    return statusMap[status] || 'Unknown';
  };

  // Request notification permission on component mount
  useEffect(() => {
    const requestNotificationPermission = async () => {
      if (!("Notification" in window)) {
        console.log("This browser does not support desktop notifications");
        return;
      }

      try {
        const permission = await Notification.requestPermission();
        setNotificationPermission(permission);
      } catch (err) {
        console.error("Error requesting notification permission:", err);
      }
    };

    requestNotificationPermission();
  }, []);

  useEffect(() => {
    const socketUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:8080'
      : 'https://api.codebreakers.online';

    const newSocket = io(socketUrl, {
      transports: ['websocket'],
      path: '/socket.io',
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      withCredentials: true
    });

    newSocket.on('connect', () => {
      console.log('Socket initialized with ID:', newSocket.id);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, []); 

  useEffect(() => {
    if (!socket) {
      console.log('⚠️ No socket connection available');
      return;
    }

    socket.on('connect', () => {
      console.log('🔌 Socket connected with ID:', socket.id);
    });

    socket.on('disconnect', () => {
      console.log('❌ Socket disconnected');
    });

    socket.on('orderStatusUpdate', (data) => {
      console.log('📩 Received orderStatusUpdate:', {
        orderId: data.orderId,
        newStatus: data.newStatus,
        details: data.orderDetails,
        timestamp: new Date().toISOString()
      });
      
      try {
        // Validate that we have the required order information
        const orderNumber = data.orderDetails?.orderid || data.orderId;
        const status = data.newStatus;

        // Only proceed if we have both a valid order number and status
        if (!orderNumber || status === undefined) {
          console.warn('⚠️ Skipping notification due to missing order information:', {
            orderNumber,
            status
          });
          return;
        }

        console.log('🔊 Attempting to play notification sound');
        notificationSound.currentTime = 0;
        notificationSound.play()
          .then(() => console.log('✅ Notification sound played successfully'))
          .catch(error => {
            console.warn('⚠️ Error playing notification sound:', error);
          });

        const mongoId = data.orderDetails?._id || data.orderId;
        console.log('MongoDB ID for navigation:', mongoId);

        const newNotification = {
          id: Date.now(),
          type: 'ORDER_STATUS',
          mongoId: mongoId,
          displayId: data.orderDetails?.orderid || data.orderId,
          status: data.newStatus,
          message: `Order #${data.orderDetails?.orderid || data.orderId} status has been updated to ${getStatusText(data.newStatus)}`,
          time: new Date().toLocaleTimeString(),
          read: false
        };

        console.log('📝 Created new notification:', newNotification);

        if (data.newStatus === 5) {
          setCurrentOrder({
            id: mongoId,
            productId: data.orderDetails?.details?.[0]?.productId,
            orderNumber: data.orderDetails?.orderid || data.orderId
          });
          setShowReviewPrompt(true);
        }

        setNotifications(prev => {
          
          console.log('📊 Current notifications count:', prev.length);
          return [newNotification, ...prev];
        });
        
        setUnreadCount(prev => {
          console.log('📊 Updating unread count from:', prev, 'to:', prev + 1);
          return prev + 1;
        });

        if (Notification.permission === 'granted' && document.hidden) {
          const notification = new Notification('Order Status Update', {
            body: newNotification.message,
            icon: '/images/logo.png',
            data: { orderId: mongoId },
            requireInteraction: true
          });

          notification.onclick = () => {
            window.focus();
            navigate(`/order/${mongoId}`);
            notification.close();
          };
        }

      } catch (error) {
        console.error('❌ Error handling notification:', error);
      }
    });

    return () => {
      console.log('🧹 Cleaning up socket listeners');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('orderStatusUpdate');
    };
  }, [socket, navigate]);

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.read) {
        markAsRead(notification.id);
      }
      setShowDropdown(false);
      navigate(`/order/${notification.mongoId}`);
      console.log('🔄 Navigating to order:', notification.mongoId);
    } catch (error) {
      console.error('❌ Error navigating to order details:', error);
    }
  };

  const handleReviewPromptAction = (action) => {
    setShowReviewPrompt(false);
    if (action === 'review' && currentOrder) {
      navigate(`/order/${currentOrder.id}`);
    }
  };

  return (
    <>


  <div 
    className="cursor-pointer flex items-center justify-center w-10 h-10 rounded-full bg-[#DBC054] hover:bg-[#c9af4a] transition-all duration-200"
    onClick={() => setShowDropdown(!showDropdown)}
  >
    <Bell className={`h-5 w-5 text-black ${isBlinking ? 'animate-bounce' : ''}`} />
    {unreadCount > 0 && (
      <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center text-xs font-medium">
        {unreadCount}
      </span>
    )}
  </div>
  

        {showDropdown && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
            <div className="p-3 border-b bg-gray-50">
              <h3 className="font-semibold text-sm">Order Notifications</h3>
            </div>
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No new notifications
              </div>
            ) : (
              notifications.map(notification => (
                <div 
                  key={notification.id} 
                  className={`p-3 border-b hover:bg-gray-50 transition-colors duration-200 ${
                    !notification.read ? 'bg-blue-50' : ''
                  } cursor-pointer`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm text-gray-600">{notification.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNotification(notification.id);
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1 ml-2 transition-colors duration-200"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      

      {/* Modified Review Prompt Dialog */}
      <CustomDialog open={showReviewPrompt} onClose={() => setShowReviewPrompt(false)}>
        <div className="p-6 space-y-4">
          <div className="text-lg font-semibold mb-2">Share Your Experience</div>
          <p className="text-center text-gray-600">
            Would you like to review your order #{currentOrder?.orderNumber}? Your feedback helps others make informed decisions.
          </p>
          <div className="flex justify-center space-x-4 mt-4">
            <button
              onClick={() => handleReviewPromptAction('skip')}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={() => handleReviewPromptAction('review')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Write a Review
            </button>
          </div>
        </div>
      </CustomDialog>
    </>
  );
};

export default UserNotifications;